import React, { useEffect, useState } from 'react'
import { Modal, ContextNotification } from '@pandiafi/components'
import { Button } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'

import type { CoreParkingSpotReservation } from '../../../common/validations/contractParkingSpotsSchema'

import { useAPICancelContractParkingSpotReservation } from 'api/apiHooks/useAPICancelContractParkingSpotReservation'
import { useCurrentContract } from 'hooks/useCurrentContract/useCurrentContract'
import { renderDate } from 'utils'

export const ParkingSpotCancellationForm: React.FC<{
  rentableParkingSpotId: number
  reservation: CoreParkingSpotReservation
  onClose: () => void
}> = ({ rentableParkingSpotId, reservation, onClose }) => {
  const { t } = useTranslation(['parking', 'common'])
  const { currentContractId } = useCurrentContract()

  const [error, setError] = useState<string | null>(null)

  const [cancelled, setCancelled] = useState(false)

  useEffect(() => {
    setError(null)
    setCancelled(false)
  }, [rentableParkingSpotId])

  const { mutate: cancel, isPending: isCancelling } =
    useAPICancelContractParkingSpotReservation(currentContractId)

  const submit = () => {
    if (!currentContractId) {
      setError(t('parking:error.contractMissing'))
      return
    }

    setError(null)
    cancel(
      { rentableParkingSpotId: rentableParkingSpotId },
      {
        onSuccess: () => {
          setCancelled(true)
        },
        onError: () => {
          setError(t('parking:cancel.error.failed'))
        },
      }
    )
  }

  return (
    <>
      {error && (
        <>
          <Modal.Notifications>
            <ContextNotification type="error">{error}</ContextNotification>
          </Modal.Notifications>
          <Modal.Buttons>
            <Button variant="cancel" onClick={onClose}>
              {t('common:closeButton')}
            </Button>
          </Modal.Buttons>
        </>
      )}

      {cancelled ? (
        <>
          <Modal.Notifications>
            <ContextNotification type="success" aria-live="polite">
              {t('parking:cancel.success')}
            </ContextNotification>
          </Modal.Notifications>
          <Modal.Buttons>
            <Button variant="cancel" onClick={onClose}>
              {t('common:closeButton')}
            </Button>
          </Modal.Buttons>
        </>
      ) : (
        <>
          <Modal.Content>
            <>
              <p className="mb-6">
                {t('parking:cancel.futureReservationLabel')}
              </p>
              <p className="font-medium">
                {t('parking:cancel.reservationStartedOn', {
                  date: renderDate(reservation.startDate),
                })}
              </p>
            </>
          </Modal.Content>
          <Modal.Buttons>
            <Button variant="cancel" onClick={onClose}>
              {t('form.actions.cancel')}
            </Button>
            <Button variant="delete" onClick={submit} loading={isCancelling}>
              {t('parking:cancel.cta')}
            </Button>
          </Modal.Buttons>
        </>
      )}
    </>
  )
}
